/**
 * U · Use Case Intelligence — the catalog of real-world use-cases / scenarios
 * mapped to their trigger regulation clusters + key issues, with coverage-matrix
 * status (ADR-0008 / RFC-0001). Replaces the old config→rule traceability stub.
 *
 * Honest: only declared-scope rows (complete / partial) show canonical-rule
 * backing; planned / out_of_scope_v0_1 rows are shown at their TRUE status and
 * never claimed covered. Public-case signals (when present) are advisory and
 * never a legal source. Pure presentation over the seed catalog — no evaluator
 * coupling.
 */

import type {
  ComplianceRequirementCard,
  CoverageMatrixStatus,
  UseCaseEntry,
  UseCasePublicSignal,
} from "@regpulse/contracts";

import { USE_CASE_CATALOG } from "../data/use-case-catalog";
import { filterUseCaseCatalog } from "../data/filter-use-case-catalog";
import { ENFORCEMENT_CASES } from "../data/enforcement-cases";

const STATUS_ORDER: CoverageMatrixStatus[] = [
  "complete",
  "partial",
  "planned",
  "out_of_scope_v0_1",
];

const STATUS_LABEL: Record<CoverageMatrixStatus, string> = {
  complete: "Complete · declared scope",
  partial: "Partial · declared scope",
  planned: "Planned",
  out_of_scope_v0_1: "Out of scope · v0.1",
};

// In-product, plain-language explanation of each coverage status (rendered under the group
// header) so the catalog is legible without knowing the governance vocabulary.
const STATUS_CAPTION: Record<CoverageMatrixStatus, string> = {
  complete:
    "Fully backed by a reviewer-verified canonical rule (the REG-… id on each row); part of the declared-scope baseline.",
  partial:
    "Canonical-backed, but the use-case is broader than the verified rule covers.",
  planned:
    "Regulation cluster is in force, but not declared-complete in v0.1 — no canonical backing yet (never claimed covered).",
  out_of_scope_v0_1:
    "Known but intentionally out of scope for v0.1 — tracked here, never claimed covered.",
};

function isDeclared(s: CoverageMatrixStatus): boolean {
  return s === "complete" || s === "partial";
}

interface Props {
  /**
   * Applicable obligation cards from the current evaluation. When set, the coverage-gap map is
   * deduped against ABCD so U does not re-list REG-* rows or planned scenarios already on board.
   */
  boardCards?: ComplianceRequirementCard[];
  /**
   * D4: advisory public enforcement signals for the "Case intelligence" section. Defaults to the
   * owner's EU enforcement caseset (advisory + unverified). Accepted-to-U signals are appended by
   * the parent. NEVER board obligations, never coverage.
   */
  caseSignals?: UseCasePublicSignal[];
}

const U_CATALOG_STATUSES: CoverageMatrixStatus[] = STATUS_ORDER.filter((s) => s !== "complete");

export function UseCaseIntelligence({
  boardCards,
  caseSignals = ENFORCEMENT_CASES,
}: Props = {}) {
  const visibleCatalog = filterUseCaseCatalog(USE_CASE_CATALOG, { boardCards });
  const groups = U_CATALOG_STATUSES.map((status) => ({
    status,
    entries: visibleCatalog.filter((e) => e.coverageStatus === status),
  })).filter((g) => g.entries.length > 0);
  const boardDeduped = Boolean(boardCards?.length);

  return (
    <section
      data-testid="use-case-intelligence"
      aria-labelledby="uci-title"
      style={{
        background: "var(--color-surface-elevated)",
        border: "1px solid var(--color-border)",
        borderRadius: "var(--radius-8)",
        padding: "var(--space-12)",
      }}
    >
      <header style={{ marginBottom: "var(--space-8)" }}>
        <h3 id="uci-title" className="type-h3" style={{ margin: 0, fontSize: 13 }}>
          U · Use Case Intelligence
        </h3>
        <p
          className="type-caption"
          style={{ margin: "var(--space-4) 0 0 0", color: "var(--color-text-secondary)" }}
        >
          Cross-cutting scenario landscape — <strong>not</strong> a second copy of the A–D
          obligation board. After you run applicability, rows that already have an obligation card
          above are <strong>hidden here</strong>; only true scenario gaps remain, plus advisory
          enforcement signals. Not a legal determination.
        </p>
        <p
          className="type-caption"
          style={{ margin: "var(--space-4) 0 0 0", color: "var(--color-text-secondary)" }}
        >
          <strong>Two parts:</strong> ① coverage-gap map (below, board-deduped when evaluated); ②
          public <em>enforcement signals</em> (advisory). Neither is a board obligation.
        </p>
        {boardDeduped && groups.length === 0 && (
          <p
            data-testid="uci-map-empty"
            className="type-caption"
            style={{ margin: "var(--space-8) 0 0 0", color: "var(--color-text-secondary)" }}
          >
            No extra scenario gaps for this configuration — declared obligations are on the board
            above. Case intelligence (if any) is still shown below.
          </p>
        )}
      </header>

      <div style={{ display: "grid", gap: "var(--space-12)" }}>
        {groups.map((g) => (
          <div key={g.status} data-testid={`uci-group-${g.status}`}>
            <h4
              className="type-caption"
              style={{
                margin: "0 0 var(--space-4) 0",
                fontSize: 11,
                fontWeight: 600,
                color: isDeclared(g.status)
                  ? "var(--color-text-primary)"
                  : "var(--color-text-secondary)",
              }}
            >
              {STATUS_LABEL[g.status]} · {g.entries.length}
            </h4>
            <p
              data-testid={`uci-caption-${g.status}`}
              className="type-caption"
              style={{ margin: "0 0 var(--space-4) 0", fontSize: 10, color: "var(--color-text-secondary)" }}
            >
              {STATUS_CAPTION[g.status]}
            </p>
            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: "var(--space-4)" }}>
              {g.entries.map((e) => (
                <UseCaseRow key={e.id} entry={e} />
              ))}
            </ul>
          </div>
        ))}
      </div>

      <CaseIntelligenceSection signals={caseSignals} />
    </section>
  );
}

/**
 * D4 · Case intelligence — advisory public enforcement signals (ADR-0008/RFC-0003 §4). Every
 * signal is advisory; `reviewedBy === null` renders an "unverified" chip. Never a legal source,
 * never a board obligation, never coverage. Summaries are reviewer-authored / "reported"-framed.
 */
function CaseIntelligenceSection({ signals }: { signals: UseCasePublicSignal[] }) {
  return (
    <div data-testid="case-intelligence" style={{ marginTop: "var(--space-16)" }}>
      <h4 className="type-caption" style={{ margin: "0 0 var(--space-4) 0", fontSize: 11, fontWeight: 600 }}>
        Case intelligence · public enforcement signals · {signals.length}
      </h4>
      <p
        data-testid="case-intel-disclaimer"
        className="type-caption"
        style={{ margin: "0 0 var(--space-8) 0", color: "var(--color-text-disabled)", fontStyle: "italic" }}
      >
        Advisory public-reporting signals — reviewer-unverified until signed; amounts / dates /
        characterisations are "reported" / "alleged", not asserted as fact, and never a legal
        determination or coverage claim (ADR-0002 / ADR-0008).
      </p>
      {signals.length === 0 ? (
        <p style={{ margin: 0, fontSize: 11, color: "var(--color-text-secondary)", fontStyle: "italic" }}>
          No case-intelligence signals yet. Accept a discovered lead to U, or run discovery.
        </p>
      ) : (
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: "var(--space-4)" }}>
          {signals.map((s, i) => (
            <CaseSignalRow key={`${s.sourceUrl}-${i}`} signal={s} />
          ))}
        </ul>
      )}
    </div>
  );
}

function CaseSignalRow({ signal }: { signal: UseCasePublicSignal }) {
  const unverified = signal.reviewedBy === null;
  return (
    <li
      data-testid="case-intel-signal"
      data-reviewed={unverified ? "false" : "true"}
      style={{
        border: "1px solid var(--color-border)",
        borderLeft: `4px solid var(--color-border-strong)`,
        borderRadius: "var(--radius-4)",
        padding: "var(--space-8)",
        background: "var(--color-surface)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: "var(--space-8)", alignItems: "baseline" }}>
        <span style={{ fontSize: 12 }}>{signal.summary}</span>
        <span
          className="type-mono"
          style={{
            fontSize: 10,
            padding: "0 var(--space-4)",
            borderRadius: "var(--radius-4)",
            border: "1px dashed var(--color-border-strong)",
            color: "var(--color-text-disabled)",
            whiteSpace: "nowrap",
          }}
        >
          {unverified ? "unverified" : `reviewed by ${signal.reviewedBy}`}
        </span>
      </div>
      <div style={{ marginTop: "var(--space-4)", fontSize: 10 }}>
        <a href={signal.sourceUrl} target="_blank" rel="noreferrer" style={{ color: "var(--color-text-secondary)" }}>
          candidate source
        </a>
        {signal.useCaseId && (
          <span className="type-mono" style={{ marginLeft: "var(--space-8)", color: "var(--color-text-disabled)" }}>
            ↳ {signal.useCaseId}
          </span>
        )}
      </div>
    </li>
  );
}

function UseCaseRow({ entry }: { entry: UseCaseEntry }) {
  const declared = isDeclared(entry.coverageStatus);
  return (
    <li
      data-testid={`uc-entry-${entry.id}`}
      data-coverage={entry.coverageStatus}
      style={{
        border: "1px solid var(--color-border)",
        borderLeft: `4px solid ${declared ? "var(--color-accent)" : "var(--color-border-strong)"}`,
        borderRadius: "var(--radius-4)",
        padding: "var(--space-8)",
        background: "var(--color-surface)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: "var(--space-8)", alignItems: "baseline" }}>
        <strong style={{ fontSize: 13 }}>{entry.useCase}</strong>
        <span
          className="type-mono"
          style={{
            fontSize: 10,
            padding: "0 var(--space-4)",
            borderRadius: "var(--radius-4)",
            border: declared ? "1px solid var(--color-border)" : "1px dashed var(--color-border-strong)",
            color: declared ? "var(--color-text-secondary)" : "var(--color-text-disabled)",
            whiteSpace: "nowrap",
          }}
        >
          {entry.coverageStatus}
        </span>
      </div>
      <div style={{ marginTop: "var(--space-4)", fontSize: 11, color: "var(--color-text-secondary)" }}>
        {entry.regulationClusters.join(" · ")}
      </div>
      {entry.keyIssues.length > 0 && (
        <div style={{ marginTop: "var(--space-4)", fontSize: 11, color: "var(--color-text-secondary)" }}>
          {entry.keyIssues.join(" · ")}
        </div>
      )}
      {entry.canonicalRuleIds.length > 0 && (
        <div
          className="type-mono"
          style={{ marginTop: "var(--space-4)", fontSize: 10, color: "var(--color-text-secondary)" }}
        >
          canonical: {entry.canonicalRuleIds.join(", ")}
        </div>
      )}
      {entry.publicCaseSignal && (
        <div style={{ marginTop: "var(--space-4)", fontSize: 11 }}>
          <span style={{ color: "var(--color-text-disabled)", fontStyle: "italic" }}>
            advisory signal{entry.publicCaseSignal.reviewedBy ? "" : " · unverified"}:
          </span>{" "}
          {entry.publicCaseSignal.summary}{" "}
          <a href={entry.publicCaseSignal.sourceUrl} target="_blank" rel="noreferrer" style={{ fontSize: 10 }}>
            source
          </a>
        </div>
      )}
    </li>
  );
}
